import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { helpUrlFor } from './help-links';

/**
 * Floating `?` button that opens the user guide at the page for the current screen.
 *
 * One button in the layout, not one per page: new screens get help by adding a line to
 * `help-links.ts`, and no page template needs to change. It opens in a new tab so a
 * half-filled form is never lost.
 */
@Component({
  selector: 'app-help-button',
  standalone: true,
  template: `
    <a
      [href]="helpUrl()"
      target="_blank"
      rel="noopener"
      aria-label="Open the user guide for this page"
      title="Help"
      class="fixed bottom-6 right-6 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-contrast shadow-lg no-underline transition-transform hover:scale-105"
    >
      <i class="pi pi-question text-lg"></i>
    </a>
  `,
})
export class HelpButtonComponent {
  private readonly router = inject(Router);

  private readonly routerUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  protected readonly helpUrl = () => helpUrlFor(this.routerUrl(), environment.userGuideUrl);
}
